/**
 * 門市菜單 Vuex Module
 * 管理門市菜單資料（使用 sheet_url）
 * 只包含：分類、餐點、套餐
 */

import * as storeMenuApi from '@/services/storeMenu.service';
import { productKeyMap } from '@/static/docs/menuKeyMap';

export default {
    namespaced: true,

    state: {
        // 原始回應資料
        categoriesResponse: [],
        productsResponse: [],
        combosResponse: [],

        // Sheet 名稱（動態取得）
        sheetNames: {
            categories: '',
            combos: '',
            products: ''
        },

        // 載入狀態
        isLoading: false,
        errorMessage: null,

        // 資料載入標記
        dataLoaded: {
            categories: false,
            products: false,
            combos: false
        }
    },

    getters: {
        /**
         * 取得分類列表（已格式化）
         */
        categories: (state) => {
            const result = [];

            for (let i = 2; i < state.categoriesResponse.length; i++) {
                const [categoryId, categoryName, enable, ...categoryItems] = state.categoriesResponse[i];

                result.push({
                    categoryId,
                    categoryName,
                    enable: enable === 'true',
                    categoryItems: categoryItems.filter(item => item)
                });
            }

            return result.filter(category => {
                return category.enable && category.categoryId;
            });
        },

        /**
         * 取得商品列表（已格式化）
         */
        products: (state) => {
            let data = [...state.productsResponse];

            if (data.length < 2) return [];

            const headers = data[1];
            const result = [];

            // 將每個 row 長度補齊與 headers 一樣長
            data = data.map(row => {
                const diff = headers.length - row.length;
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        row.push(null);
                    }
                }
                return row;
            });

            for (let i = 2; i < data.length; i++) {
                const item = {};

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index];
                    const key = productKeyMap[headers[index]];

                    if (key && !key.includes('specSetting')) {
                        item[key] = value;
                    }
                }

                // 只加入有 ID 且允許販售的商品
                if (item.id && item.availableInStore === 'true') {
                    result.push(item);
                }
            }

            return result;
        },

        /**
         * 取得套餐列表（已格式化）
         * 只讀取前三個欄位：套餐 ID、名稱、允許販售
         */
        combos: (state) => {
            let data = [...state.combosResponse];

            if (data.length < 2) {
                return [];
            }

            const result = [];

            // 直接使用索引讀取前三個欄位：套餐 ID (0)、名稱 (1)、允許販售 (2)
            for (let i = 2; i < data.length; i++) {
                const [id, name, availableInStore] = data[i];

                // 只加入有 ID 且允許販售的套餐
                if (id && availableInStore === 'true') {
                    result.push({
                        id,
                        name,
                        availableInStore
                    });
                }
            }

            return result;
        },

        /**
         * 根據 ID 取得商品
         */
        getProductById: (state, getters) => (productId) => {
            return getters.products.find(product => product.id === productId);
        },

        /**
         * 根據 ID 取得套餐
         */
        getComboById: (state, getters) => (comboId) => {
            return getters.combos.find(combo => combo.id === comboId);
        },

        /**
         * 根據分類 ID 取得商品
         */
        getProductsByCategoryId: (state, getters) => (categoryId) => {
            const category = getters.categories.find(cat => cat.categoryId === categoryId);
            if (!category) return [];

            return getters.products.filter(product =>
                category.categoryItems.includes(product.id)
            );
        },

        /**
         * 根據分類 ID 取得套餐
         */
        getCombosByCategoryId: (state, getters) => (categoryId) => {
            const category = getters.categories.find(cat => cat.categoryId === categoryId);
            if (!category) return [];

            return getters.combos.filter(combo =>
                category.categoryItems.includes(combo.id)
            );
        },

        /**
         * 載入狀態
         */
        isLoading: (state) => state.isLoading,

        /**
         * 錯誤訊息
         */
        errorMessage: (state) => state.errorMessage,

        /**
         * 檢查資料是否已載入
         */
        isDataLoaded: (state) => (dataType) => {
            return state.dataLoaded[dataType] || false;
        },

        /**
         * 檢查所有資料是否已載入
         */
        isAllDataLoaded: (state) => {
            return state.dataLoaded.categories &&
                state.dataLoaded.products &&
                state.dataLoaded.combos;
        }
    },

    mutations: {
        /**
         * 設定分類資料
         */
        setCategories(state, payload) {
            state.categoriesResponse = payload;
            state.dataLoaded.categories = true;
        },

        /**
         * 設定商品資料
         */
        setProducts(state, payload) {
            state.productsResponse = payload;
            state.dataLoaded.products = true;
        },

        /**
         * 設定套餐資料
         */
        setCombos(state, payload) {
            state.combosResponse = payload;
            state.dataLoaded.combos = true;
        },

        /**
         * 設定載入狀態
         */
        setLoading(state, isLoading) {
            state.isLoading = isLoading;
        },

        /**
         * 設定錯誤訊息
         */
        setErrorMessage(state, message) {
            state.errorMessage = message;
        },

        /**
         * 設定 Sheet 名稱
         */
        setSheetNames(state, sheetNames) {
            state.sheetNames = sheetNames;
        },

        /**
         * 清空所有資料
         */
        clearAllData(state) {
            state.categoriesResponse = [];
            state.productsResponse = [];
            state.combosResponse = [];

            state.dataLoaded = {
                categories: false,
                products: false,
                combos: false
            };
        }
    },

    actions: {
        /**
         * 取得 Sheet 名稱
         */
        async fetchSheetNames({ commit }) {
            try {
                const sheetNames = await storeMenuApi.getStoreMenuSheets();
                commit('setSheetNames', sheetNames);
                console.log('門市菜單 Sheet 名稱已設定:', sheetNames);
                return sheetNames;
            } catch (error) {
                console.error('fetchSheetNames error:', error);
                throw error;
            }
        },

        /**
         * 取得分類資料
         */
        async fetchCategories({ state, dispatch, commit }) {
            try {
                // 如果還沒有 Sheet 名稱，先取得
                if (!state.sheetNames.categories) {
                    await dispatch('fetchSheetNames');
                }

                const response = await storeMenuApi.getStoreMenuSheetData(
                    state.sheetNames.categories
                );

                const data = response?.data?.values || [];
                commit('setCategories', data);

                console.log('門市菜單 - 分類資料載入完成:', data.length - 2, '筆');
                return data;
            } catch (error) {
                console.error('fetchCategories error:', error);
                throw error;
            }
        },

        /**
         * 取得商品資料
         */
        async fetchProducts({ state, dispatch, commit }) {
            try {
                // 如果還沒有 Sheet 名稱，先取得
                if (!state.sheetNames.products) {
                    await dispatch('fetchSheetNames');
                }

                const response = await storeMenuApi.getStoreMenuSheetData(
                    state.sheetNames.products
                );

                const data = response?.data?.values || [];
                commit('setProducts', data);

                console.log('門市菜單 - 餐點資料載入完成:', data.length - 2, '筆');
                return data;
            } catch (error) {
                console.error('fetchProducts error:', error);
                throw error;
            }
        },

        /**
         * 取得套餐資料
         */
        async fetchCombos({ state, dispatch, commit }) {
            try {
                // 如果還沒有 Sheet 名稱，先取得
                if (!state.sheetNames.combos) {
                    await dispatch('fetchSheetNames');
                }

                const response = await storeMenuApi.getStoreMenuSheetData(
                    state.sheetNames.combos
                );

                const data = response?.data?.values || [];
                commit('setCombos', data);

                console.log('門市菜單 - 套餐資料載入完成:', data.length - 2, '筆');
                return data;
            } catch (error) {
                console.error('fetchCombos error:', error);
                throw error;
            }
        },

        /**
         * 取得所有門市菜單資料
         */
        async fetchAllStoreMenuData({ dispatch, commit }) {
            commit('setLoading', true);
            commit('setErrorMessage', null);

            try {
                // 先取得 Sheet 名稱
                await dispatch('fetchSheetNames');

                // 並行取得所有資料
                await Promise.all([
                    dispatch('fetchCategories'),
                    dispatch('fetchProducts'),
                    dispatch('fetchCombos')
                ]);

                console.log('門市菜單 - 所有資料載入完成');

            } catch (error) {
                console.error('fetchAllStoreMenuData error:', error);
                commit('setErrorMessage', '載入門市菜單失敗，請稍後再試');
                throw error;
            } finally {
                commit('setLoading', false);
            }
        },

        /**
         * 清空所有資料
         */
        clearData({ commit }) {
            commit('clearAllData');
            console.log('門市菜單 - 資料已清空');
        }
    }
};
